import * as React from "react";
import {
    BackButton, Badge,
    classNames, CodeDigits, IconNames, KeyPad, layout, MaterialIcon, PushButton, TextInput, theme, typography,
    useStorageItemValue, useTypeRef,
    View,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle
} from "@variocube/cube-ui";
import {useLocalization} from "../i18n";
import {useEffect, useMemo, useRef, useState} from "react";
import {Tenant} from "./types";
import {keyframes, stylesheet} from "typestyle";

interface ReturnDeliveryVerifyProps {
    onNext: () => void;
    onBack: () => void;
    verifyPhone?: string;
    verifyEmail?: string;
}

export function ReturnDeliveryVerify(props: ReturnDeliveryVerifyProps) {
    const {onNext, onBack, verifyEmail, verifyPhone} = props;
    const {t, language} = useLocalization();
    const input = useRef<HTMLInputElement | null>();
    const [type, setType] = useTypeRef();

    const tenant = useStorageItemValue<Tenant>("tenant");
    const backendUrl = useStorageItemValue<string>('backendUrl');

    const [code, setCode] = useState<string>();
    const [enteredCode, setEnteredCode] = useState("");

    const verified = useMemo<boolean>(() => Boolean(code) && enteredCode == code, [code, enteredCode]);

    useEffect(() => {
        if(tenant && backendUrl && (verifyPhone || verifyEmail)) {
            const verifyUrl = `${backendUrl}public/tenants/${tenant.centerId}/verify-contact?language=${language}`;
            window.fetch(verifyUrl, {
                method: "POST",
                body: JSON.stringify({
                    phone: verifyPhone,
                    email: verifyEmail
                }),
                headers: { "Content-type": "application/json" }
            }).then((response) => {
                if(response.status === 200) {
                    response.json().then((json) => {
                        setCode(json.code);
                    });
                }
            });
        }
    }, [tenant, backendUrl, verifyPhone, verifyEmail]);

    useEffect(() => {
        if(verified) {
            onNext();
        }
    }, [verified, onNext]);

    return (
        <View className={classNames(layout.center, layout.alignCenter)}>
            <div className={classNames(layout.horizontal, styles.content)}>
                <div className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.box)}>
                    <div>
                        <div className={styles.title}>
                            {t("EnterVerificationCode")}
                        </div>
                        <p>
                            {t("EnterVerificationCodeInfo")}
                        </p>
                        {verifyPhone &&
                            <p>
                                {t("EnterVerificationCodePhoneInfo")}
                            </p>
                        }
                        {verifyEmail &&
                            <p>
                                {t("EnterVerificationCodeEmailInfo")}
                            </p>
                        }
                    </div>
                    {code && !verified && (enteredCode.length > 0 && enteredCode.length < code.length) && (
                        <p className={typography.muted}>
                            {t("MoreDigitsNeeded")}
                        </p>
                    )}
                    {code && !verified && enteredCode.length == code.length && (
                        <Badge error>
                            {t("VerificationCodeWrong")}
                        </Badge>
                    )}
                    <div>
                        <PushButton onClick={onBack}>
                            {t("Back")}
                        </PushButton>
                    </div>
                </div>

                <div className={classNames(theme.moreContrast)}>
                    <TextInput
                        type={verified ? "password" : "text"}
                        onChange={setEnteredCode}
                        value={enteredCode}
                        disabled={verified}
                        inputRef={input}
                        typeRef={setType}
                        placeholder={t("VerificationCodePlaceholder")}
                        className={classNames(styles.textInput)}
                    />
                    <div className={styles.keyPadContainer}>
                        <KeyPad
                            onType={type}
                            digits={CodeDigits}
                            disabled={verified}
                        />
                    </div>
                </div>
            </div>
        </View>
    );
}

const styles = stylesheet({
    content: {
        position: "relative"
    },
    title: {
        fontSize: "1.5em",
    },
    box: {
        width: "20em",
        padding: "1.5em"
    },
    keyPadContainer: {
        position: "relative",
    },
    verifiedOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "var(--color-success)",
        animationName: keyframes({
            from: { opacity: 0 },
            to: { opacity: 1 }
        }),
        animationDelay: "0s",
        animationDuration: "0.5s",
        animationFillMode: "both",
    },
    verifiedOverlayContent: {
        display: "block",
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translateX(-50%) translateY(-50%)",
    },
    checkIcon: {
        fontSize: "4rem",
        lineHeight: 1,
    },
    textInput: {
        fontWeight: "bold",
        width: "24rem",
        height: "5rem",
    },
});